import { KeyboardEvent } from 'react';

import { observer } from 'mobx-react-lite';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { IconSSO } from '@components/icons/sso';
import { PasswordField } from '@components/password-field';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';

import { UnauthedPage } from 'components/unauthed-page';
import { Modals, useModal } from 'features/modals';

import { HalloweenSigninLayout } from './halloween/halloween-signin-layout';
import { SIGNIN_EMAIL_ID, SIGNIN_PASSWORD_ID, SigninSceneState } from './signin-form-state';
import type { SigninFormState } from './use-signin-form';

export type SigninProps = {
  form: SigninFormState;
  scene: SigninSceneState;
  /** Shows the form beside the Halloween scene instead of the regular branded panel. */
  isHalloween: boolean;
};

export const Signin = observer(({ form, scene, isHalloween }: SigninProps) => {
  const { t } = useScopedTranslate(locale);
  const { showModal } = useModal();

  function handleKeyPress(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      form.onSubmit();
    }
  }

  function ssoModal() {
    // @ts-ignore SSO_SIGNIN modal is not properly typed
    showModal('SSO_SIGNIN', { email: form.email });
  }

  const content = (
    <>
      <Modals />
      <Stack space={4} onFocus={form.onFocus} onBlur={form.onBlur}>
        <Stack orientation="horizontal" justify="center">
          <Text font="heading1" text={t('heading')} />
        </Stack>
        <Stack space={4}>
          <Button
            iconLeft={<IconSSO size="24" />}
            isFullWidth
            onClick={ssoModal}
            text={t('signinMicrosoftEnterprise')}
            variant="secondary"
          />
        </Stack>
        <Stack space={2} orientation="horizontal" justify="center" align="center">
          <Separator />
          <Text font="semibold14" color="textSecondary" text={t('or')} />
          <Separator />
        </Stack>
        <Stack space={4}>
          <TextField
            id={SIGNIN_EMAIL_ID}
            placeholder={t('emailPlaceholder')}
            label={t('core.email')}
            autoComplete="email"
            autoFocus
            error={form.errors.email}
            value={form.email}
            onChange={form.onEmailChange}
            isDisabled={form.isSubmitting}
            onKeyPress={handleKeyPress}
          />

          <PasswordField
            id={SIGNIN_PASSWORD_ID}
            placeholder={t('passwordPlaceholder')}
            label={t('password')}
            autoComplete="current-password"
            error={form.errors.password}
            value={form.password}
            onChange={form.onPasswordChange}
            isPasswordVisible={form.isPasswordVisible}
            onPasswordVisibilityChange={form.onPasswordVisibilityChange}
            isDisabled={form.isSubmitting}
            onKeyPress={handleKeyPress}
          />

          <Button
            isDisabled={form.isSubmitting}
            onClick={form.onSubmit}
            text={t('signin')}
            variant="primary-blue"
            dataTestid="signin-button"
          />
        </Stack>
        <Stack space={2} align="center">
          <ActionText.Link
            to="/forgotpassword"
            children={t('forgotPassword')}
            font="regular14"
            color="purple60"
          />
        </Stack>
      </Stack>
    </>
  );

  if (isHalloween) {
    return <HalloweenSigninLayout scene={scene}>{content}</HalloweenSigninLayout>;
  }
  return <UnauthedPage>{content}</UnauthedPage>;
});

const locale = {
  en: {
    heading: 'Welcome back!',
    password: 'Password',
    signin: 'Sign in',
    emailPlaceholder: 'Enter your email...',
    passwordPlaceholder: 'Enter password...',
    signinMicrosoftEnterprise: 'Sign in with SSO',
    forgotPassword: 'Forgot password?',
    or: 'OR',
  },
  is: {
    heading: 'Velkomi/n/ð/nn aftur!',
    password: 'Lykilorð',
    signin: 'Skrá inn',
    emailPlaceholder: 'Sláðu inn netfangið þitt...',
    passwordPlaceholder: 'Sláðu inn lykilorðið...',
    signinMicrosoftEnterprise: 'Skrá inn með SSO',
    forgotPassword: 'Gleymdirðu lykilorðinu þínu?',
    or: 'EÐA',
  },
};
